import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ListChecks, PhoneCall, Mail, Users, ShieldCheck, Building2,
  SlidersHorizontal, Upload, Settings2, PhoneIncoming, ShieldAlert, GitBranch,
} from "lucide-react";
import UsersTable from "@/components/settings/UsersTable";
import PermissionMatrix from "@/components/PermissionMatrix";
import DropdownCatalogueManager from "@/components/leadsettings/DropdownCatalogueManager";
import AssigningLogicMatrix from "@/components/leadsettings/AssigningLogicMatrix";
import DialerConfigForm from "@/components/leadsettings/DialerConfigForm";
import EmailSchedulerManager from "@/components/leadsettings/EmailSchedulerManager";
import LeadFieldsManager from "@/components/leadsettings/LeadFieldsManager";
import ImportHistoryPanel from "@/components/leadsettings/ImportHistoryPanel";
import CallLogImportPanel from "@/components/leadsettings/CallLogImportPanel";
import OutcomeMappingPanel from "@/components/leadsettings/OutcomeMappingPanel";
import AgentAliasPanel from "@/components/leadsettings/AgentAliasPanel";
import SystemConfigForm from "@/components/leadsettings/SystemConfigForm";
import TenantMigrationsCard from "@/components/settings/TenantMigrationsCard";
import CallScriptsManager from "@/components/leadsettings/CallScriptsManager";
import DataBackupsPanel from "@/components/leadsettings/DataBackupsPanel";
import NumberRefreshPanel from "@/components/leadsettings/NumberRefreshPanel";
import { ScrollText, Database, PhoneForwarded } from "lucide-react";

/** Ooredoo / Al-Meera Settings shell. Different tab set from the ticketing
 *  Starlink settings — no Categories/SLA/WA templates; adds Dropdown Catalogue,
 *  Field Management, Lead Upload, Dialer Config, Email Scheduler, System Config. */
const TABS = [
  { key: "users", label: "Users & Roles", icon: Users, testid: "tab-lead-users",
    perms: ["users.view"] },
  { key: "permissions", label: "Permissions", icon: ShieldCheck, testid: "tab-lead-perms",
    perms: ["users.manage_permissions"] },
  { key: "fields", label: "Field Management", icon: SlidersHorizontal, testid: "tab-lead-fields",
    perms: ["leads.manage_fields", "users.manage_permissions"] },
  { key: "catalogue", label: "Dropdown Catalogue", icon: ListChecks, testid: "tab-catalogue",
    perms: ["leads.manage_dropdowns", "catalogue.view_lookups", "catalogue.manage_issue_types"] },
  { key: "scripts", label: "Call Scripts", icon: ScrollText, testid: "tab-call-scripts",
    perms: ["leads.manage_fields", "leads.view_all", "users.manage_permissions"], campaignOnly: true },
  { key: "data", label: "Data & Backups", icon: Database, testid: "tab-data-backups",
    perms: ["module.settings", "users.manage_permissions"], campaignOnly: true },
  { key: "assigning", label: "Assigning Logic", icon: GitBranch, testid: "tab-assigning-logic",
    perms: ["leads.view_assigning_logic", "leads.manage_assigning_logic"] },
  { key: "upload", label: "Lead Upload", icon: Upload, testid: "tab-lead-upload",
    perms: ["leads.import", "users.manage_permissions"] },
  { key: "calllogs", label: "Call Log Import", icon: PhoneIncoming, testid: "tab-call-log-import",
    perms: ["leads.import_call_logs", "leads.view_call_log_imports", "users.manage_permissions"] },
  { key: "refresh", label: "Number Refresh", icon: PhoneForwarded, testid: "tab-number-refresh",
    perms: ["leads.import", "leads.view_imports", "users.manage_permissions"], clients: ["ooredoo-soho"] },
  { key: "dialer", label: "Dialer Config", icon: PhoneCall, testid: "tab-dialer",
    perms: ["leads.manage_dialer", "users.manage_permissions"] },
  { key: "scheduler", label: "Email Scheduler", icon: Mail, testid: "tab-scheduler",
    perms: ["leads.manage_schedules", "email_templates.view", "users.manage_permissions"] },
  { key: "system", label: "System Config", icon: Settings2, testid: "tab-system-config",
    perms: ["leads.manage_system_config", "users.manage_permissions"] },
];

export default function LeadSettings() {
  const { active } = useClient();
  const { hasAnyPerm } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const isCampaign = active?.kind !== "leads";   // Starlink: leads module on a ticketing tenant
  const visible = useMemo(
    () => TABS.filter((t) => hasAnyPerm(t.perms) && (!t.campaignOnly || isCampaign)
                              && (!t.clients || t.clients.includes(active?.slug))
                              && !(isCampaign && ["users", "permissions"].includes(t.key))),
    [hasAnyPerm, isCampaign, active?.slug]
  );
  const requested = searchParams.get("tab");
  const requestedBlocked =
    requested && TABS.some((t) => t.key === requested) && !visible.some((t) => t.key === requested);
  const [tab, setTab] = useState(
    (!requestedBlocked && requested && visible.some((t) => t.key === requested))
      ? requested
      : (visible[0]?.key || "users")
  );
  const changeTab = (v) => { setTab(v); setSearchParams({ tab: v }, { replace: true }); };

  if (visible.length === 0) {
    return (
      <div className="text-slate-500 p-6 text-sm" data-testid="settings-no-access">
        You don&apos;t have permission to view any Settings tab for this tenant.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="lead-settings">
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-slate-500" />
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Settings</div>
          <h1 className="font-heading text-lg font-semibold text-slate-900">{active?.name} · {isCampaign ? "Campaign settings" : "Configuration"}</h1>
        </div>
      </div>

      {requestedBlocked && (
        <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2"
             data-testid="settings-tab-no-access">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          You don&apos;t have access to the &quot;{TABS.find((t) => t.key === requested)?.label}&quot; tab.
          Showing the tabs available to you instead.
        </div>
      )}

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="flex-wrap h-auto bg-slate-100 p-1 rounded gap-1">
          {visible.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              data-testid={t.testid}
              className="data-[state=active]:bg-white data-[state=active]:text-indigo-700 text-xs h-8 px-3"
            >
              <t.icon className="w-3.5 h-3.5 mr-1.5" />
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <UsersTable />
        </TabsContent>
        <TabsContent value="permissions" className="mt-4">
          <Card>
            <CardContent className="p-4">
              <PermissionMatrix />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="fields" className="mt-4">
          <LeadFieldsManager />
        </TabsContent>
        <TabsContent value="catalogue" className="mt-4">
          <DropdownCatalogueManager />
        </TabsContent>
        <TabsContent value="scripts" className="mt-4">
          <CallScriptsManager />
        </TabsContent>
        <TabsContent value="data" className="mt-4">
          <DataBackupsPanel />
        </TabsContent>
        <TabsContent value="assigning" className="mt-4">
          <AssigningLogicMatrix />
        </TabsContent>
        <TabsContent value="upload" className="mt-4">
          <ImportHistoryPanel />
        </TabsContent>
        <TabsContent value="calllogs" className="mt-4 space-y-4">
          <CallLogImportPanel />
          <OutcomeMappingPanel />
          <AgentAliasPanel />
        </TabsContent>
        <TabsContent value="refresh" className="mt-4">
          <NumberRefreshPanel />
        </TabsContent>
        <TabsContent value="dialer" className="mt-4">
          <DialerConfigForm />
        </TabsContent>
        <TabsContent value="scheduler" className="mt-4">
          <EmailSchedulerManager />
        </TabsContent>
        <TabsContent value="system" className="mt-4">
          <SystemConfigForm />
          <div className="mt-4"><TenantMigrationsCard /></div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
